import { FormattedDateTime } from "@/shared/datetime";
import { AgentThreadRecord } from "@/shared/domain/agentThread";
import { useAgentThreadsForUser } from "@/shared/domain/agentThread/hooks";

import styles from "./AgentThreadHistoryView.module.css";
import { ThreadRowMenu } from "./ThreadRowMenu";

const AgentThreadHistoryViewRow = ({
  agentThread,
  onClick,
  onOpenThreadInFullView,
}: {
  agentThread: AgentThreadRecord;
  onClick: () => void;
  onOpenThreadInFullView: (threadId: string) => void;
}) => {
  return (
    <div className={styles.agentThreadRow} onClick={onClick}>
      <div className={styles.agentThreadHeader}>
        <p className={styles.agentThreadTitle}>
          {agentThread.title || `Thread ${agentThread.thread_id}`}
        </p>
        <ThreadRowMenu
          threadId={agentThread.thread_id}
          pinnedAt={agentThread.pinned_at}
          className={styles.rowMenuTrigger}
          onOpenThreadInFullView={onOpenThreadInFullView}
        />
      </div>
      <p className={styles.agentThreadSubtitle}>
        <FormattedDateTime value={agentThread.created} /> -{" "}
        {agentThread.thread_id}
      </p>
    </div>
  );
};

interface AgentThreadHistoryViewProps {
  /** Navigate the sidebar to the clicked thread. On the sidebar this is
   *  exactly ``open({ threadId, view: "thread" })``, which is also what leaves
   *  this pane — including when the thread clicked is the one already on
   *  screen. */
  onThreadClicked: (threadId: string) => void;

  /** Navigates to a thread's own page and closes the sidebar behind it.
   *  Forwarded to each row's overflow menu. */
  onOpenThreadInFullView: (threadId: string) => void;
}

/**
 * One labeled group of history rows. Renders nothing for an empty group, so
 * a header never sits over zero rows — that is what keeps "Pinned" off the
 * screen for a user who has not pinned anything, and would do the same for
 * "Recents" if every thread were pinned.
 */
const AgentThreadHistorySection = ({
  label,
  threads,
  onThreadClicked,
  onOpenThreadInFullView,
}: {
  label: string;
  threads: AgentThreadRecord[];
  onThreadClicked: (threadId: string) => void;
  onOpenThreadInFullView: (threadId: string) => void;
}) => {
  if (threads.length === 0) {
    return null;
  }

  return (
    <>
      <p className={styles.sectionLabel}>{label}</p>
      {threads.map((agentThread) => (
        <AgentThreadHistoryViewRow
          key={agentThread.thread_id}
          agentThread={agentThread}
          onClick={() => onThreadClicked(agentThread.thread_id)}
          onOpenThreadInFullView={onOpenThreadInFullView}
        />
      ))}
    </>
  );
};

/**
 * Pin time as epoch milliseconds, and 0 for an unpinned thread — which never
 * reaches the pinned block, so the fallback only exists to keep the optional
 * field honest. Parsing rather than comparing the strings keeps the ordering
 * right even when the server's timestamp spelling differs from the
 * `toISOString()` the optimistic pin patch stamps.
 */
const pinnedAtMillis = (thread: AgentThreadRecord) =>
  thread.pinned_at ? Date.parse(thread.pinned_at) : 0;

export const AgentThreadHistoryView = ({
  onThreadClicked,
  onOpenThreadInFullView,
}: AgentThreadHistoryViewProps) => {
  const agentThreadsQuery = useAgentThreadsForUser();

  const agentThreadHistory = agentThreadsQuery.data?.items || [];
  // The server returns pinned rows first, most recently pinned first. Sorting
  // the pinned block the same way here matters because the optimistic patch in
  // `useSetThreadPinned` stamps `pinned_at` without moving the row: without
  // this, a thread you just pinned joins the block wherever it already sat —
  // below every older pin — and only snaps to the top once the refetch lands.
  // The sort is stable, so rows sharing a pin instant keep the tie-break the
  // server sent them in (creation time, then id).
  const pinnedThreads = agentThreadHistory
    .filter((thread) => thread.pinned_at)
    .sort((a, b) => pinnedAtMillis(b) - pinnedAtMillis(a));
  const recentThreads = agentThreadHistory.filter(
    (thread) => !thread.pinned_at,
  );

  if (agentThreadsQuery.isLoading) {
    return (
      <div className={styles.mainContainer}>
        <div className={styles.centeringContainer}>
          <p>Loading threads...</p>
        </div>
      </div>
    );
  }

  if (agentThreadsQuery.error) {
    return (
      <div className={styles.mainContainer}>
        <div className={styles.centeringContainer}>
          <p>Error loading threads: {agentThreadsQuery.error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.centeringContainer}>
        {agentThreadHistory.length === 0 ? (
          <p>No threads yet!</p>
        ) : (
          <>
            <AgentThreadHistorySection
              label="Pinned"
              threads={pinnedThreads}
              onThreadClicked={onThreadClicked}
              onOpenThreadInFullView={onOpenThreadInFullView}
            />
            <AgentThreadHistorySection
              label="Recents"
              threads={recentThreads}
              onThreadClicked={onThreadClicked}
              onOpenThreadInFullView={onOpenThreadInFullView}
            />
          </>
        )}
      </div>
    </div>
  );
};
